import { getCurrentInstance, onScopeDispose } from 'vue';
import { useEventListener } from '@vueuse/core';
import { useConfirmDialog } from '@/composition/use-confirm-dialog';
import { registerReusedPageLeaveGuard } from '@/utils/navigation.utils';
import type { ICatalogPageState } from './catalog-page-state';

type TLeaveGuard = () => Promise<boolean>;

/**
 * The mounted Menú page's question. vue-router 3 has no composable route
 * guards: the page's option guards (`beforeRouteLeave`/`Update`) reach it
 * through this module.
 */
let activeGuard: TLeaveGuard | null = null;
let activeEditorGuard: TLeaveGuard | null = null;

/** True when the page may be left (or has nothing unsaved). */
export function confirmCatalogLeave(): Promise<boolean> {
  return activeGuard ? activeGuard() : Promise.resolve(true);
}

/**
 * True when the open editor may close or switch its target (in-page
 * navigations: the reorder question belongs to leaving the page).
 */
export function confirmCatalogEditorDiscard(): Promise<boolean> {
  return activeEditorGuard ? activeEditorGuard() : Promise.resolve(true);
}

/**
 * «¿Descartar los cambios en "{nombre}"?» (plan §5.5) whenever an unsaved
 * editor would be lost: leaving the page, switching the editor's target,
 * switching stores (the reused-page guard), or closing the tab. Confirming
 * discards first, so later guards of the same navigation do not ask again.
 */
export function useCatalogLeaveGuard(state: ICatalogPageState) {
  const confirmDialog = useConfirmDialog();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  /** A question already on screen answers every navigation that asks. */
  let pending: Promise<boolean> | null = null;
  let pendingReorder: Promise<boolean> | null = null;

  /** «¿Descartar el nuevo orden?» (plan §5.11). */
  async function askReorder(): Promise<boolean> {
    const accepted = await confirmDialog.confirm({
      title: t('menuPage.reorder.discardTitle'),
      message: t('menuPage.reorder.discardMessage'),
      acceptText: t('menuPage.editor.discard'),
      tone: 'danger',
    });
    if (accepted) state.reorderDirty = false;
    return accepted;
  }

  /** Leaving reorder mode with an unsaved order; true to go on. */
  function confirmReorderDiscard(): Promise<boolean> {
    if (!state.reorderDirty) return Promise.resolve(true);
    if (!pendingReorder)
      pendingReorder = askReorder().finally(() => {
        pendingReorder = null;
      });
    return pendingReorder;
  }

  async function ask(): Promise<boolean> {
    const accepted = await confirmDialog.confirm({
      title: state.editorName
        ? t('menuPage.editor.discardTitle', { name: state.editorName })
        : t('menuPage.editor.discardTitleUnnamed'),
      message: state.editorGroupsSaved
        ? `${t('menuPage.editor.discardMessage')} ${t(
            'menuPage.editor.discardGroupsSaved'
          )}`
        : t('menuPage.editor.discardMessage'),
      acceptText: t('menuPage.editor.discard'),
      tone: 'danger',
    });
    // One question per navigation: the editor counts as clean from here on.
    if (accepted) state.editorDirty = false;
    return accepted;
  }

  /** The editor's changes (the new order first, when leaving the page). */
  function confirmEditorDiscard(): Promise<boolean> {
    if (!state.editorDirty) return Promise.resolve(true);
    if (!pending)
      pending = ask().finally(() => {
        pending = null;
      });
    return pending;
  }

  async function confirmPageLeave(): Promise<boolean> {
    return (await confirmReorderDiscard()) && confirmEditorDiscard();
  }

  activeGuard = confirmPageLeave;
  activeEditorGuard = confirmEditorDiscard;
  const unregister = registerReusedPageLeaveGuard(confirmPageLeave);

  useEventListener(window, 'beforeunload', (event: BeforeUnloadEvent) => {
    if (!state.editorDirty && !state.reorderDirty) return;
    event.preventDefault();
    // Chrome < 119 needs returnValue set.
    event.returnValue = '';
  });

  onScopeDispose(() => {
    unregister();
    if (activeGuard === confirmPageLeave) activeGuard = null;
    if (activeEditorGuard === confirmEditorDiscard) activeEditorGuard = null;
  });

  return {
    confirmDiscard: confirmEditorDiscard,
    confirmReorderDiscard,
  };
}
